<template>
  <section class="hp-page">
    <HomeStickySearch
      :visible="showStickySearch"
      :filter.sync="filter"
      :course-options="courseOptions"
      :location-options="locationOptions"
      @search="getJobs"
    />

    <HomeHero
      :filter.sync="filter"
      :course-options="courseOptions"
      :location-options="locationOptions"
      @search="getJobs"
    />

    <HomeJobList
      :jobs="jobs"
      :loading="jobsLoading"
      :categories="popularCategories"
    />

    <div class="hp-engage-band">
      <HomeFeaturedPosts
        :posts="featuredPosts"
        :loading="postsLoading"
      />

      <HomeTrendingCourses :courses="trendingCourses" />
    </div>

    <HomeVisumeSection />

    <HomePopularCities :cities="popularCities" />

    <HomeEmployerBanner />

    <HomeFaq />

    <Modals :open="modals.open" />
  </section>
</template>

<script>
const Modals = () => import(/* webpackPrefetch: true */ '@/components/Modals');
import HomeHero from '@/components/home/HomeHero.vue';
import HomeStickySearch from '@/components/home/HomeStickySearch.vue';
import HomeJobList from '@/components/home/HomeJobList.vue';
import HomePopularCities from '@/components/home/HomePopularCities.vue';
import HomeVisumeSection from '@/components/home/HomeVisumeSection.vue';
import HomeFeaturedPosts from '@/components/home/HomeFeaturedPosts.vue';
import HomeTrendingCourses from '@/components/home/HomeTrendingCourses.vue';
import HomeEmployerBanner from '@/components/home/HomeEmployerBanner.vue';
import HomeFaq from '@/components/home/HomeFaq.vue';

import '@/assets/hire.css';
import '@/assets/home-page.css';
import Actions from '@/libraries/apiActions.js';
import validation from '@/libraries/validFormat';
import { mapState, mapGetters } from 'vuex';
import { COURSES } from '@/store/modules/courses/constants';
import { STATES } from '@/store/modules/states/constants';
import { attachJobParam } from '@/utils/formatJob';
import {
  POPULAR_CATEGORIES,
  TRENDING_COURSES,
  POPULAR_CITIES,
  HOME_ORG_JSONLD,
  HOME_FAQ_JSONLD,
  getHomeBreadcrumbJsonld,
} from '@/constants/homePageData';
import { buildCanonicalUrl } from '@/utils/seo';
import { scrollToSection, isUpskillingCoursesHash } from '@/utils/scrollToSection';

export default {
  name: 'HomePage',
  components: {
    Modals,
    HomeHero,
    HomeStickySearch,
    HomeJobList,
    HomePopularCities,
    HomeVisumeSection,
    HomeFeaturedPosts,
    HomeTrendingCourses,
    HomeEmployerBanner,
    HomeFaq,
  },
  metaInfo() {
    return {
      title: 'Search jobs online | Upload Video Resume | Hireclap.com',
      meta: [
        {
          name: 'description',
          content: 'Upload your video resume & find matching entry level jobs. Search and apply for the job vacancies in Government sector, IT, software, technical, banking, sales,BPO jobs etc',
        },
        {
          name: 'keywords',
          content: 'Job,Video Profile,recruitment,Resume, Entry Level, freshers jobs, government jobs,bank jobs, railway, recruitment,online ,part time, Engineering, management , technical, BPO, Finance,Accounting Jobs, campus hiring, work from home ',
        },
      ],
      link: [{ rel: 'canonical', href: buildCanonicalUrl() }],
      script: [
        { type: 'application/ld+json', json: HOME_ORG_JSONLD },
        { type: 'application/ld+json', json: HOME_FAQ_JSONLD },
        { type: 'application/ld+json', json: getHomeBreadcrumbJsonld() },
      ],
    };
  },
  data() {
    return {
      action: new Actions(),
      validate: new validation(),
      modals: { open: 'exit' },
      jobs: [],
      filter: {
        jobrole: null,
        city_id: null,
        course_id: null,
        home_display: 1,
        search: null,
      },
      jobsLoading: true,
      featuredPosts: [],
      postsLoading: true,
      popularCategories: POPULAR_CATEGORIES,
      trendingCourses: TRENDING_COURSES,
      popularCities: POPULAR_CITIES,
      showStickySearch: false,
    };
  },
  computed: {
    ...mapGetters(['cachedDispatch']),
    ...mapState({
      courseOptions: state => state.courses.courses.courses,
      locationOptions: state => state.states.locations.locations,
    }),
  },
  watch: {
    '$route.hash'() {
      this.scrollToHashSectionWhenReady();
    },
    jobsLoading(isLoading) {
      if (!isLoading) this.scrollToHashSectionWhenReady();
    },
    postsLoading(isLoading) {
      if (!isLoading) this.scrollToHashSectionWhenReady();
    },
  },
  created() {
    if (isUpskillingCoursesHash(this.$route)) {
      window.scrollTo(0, 0);
    }
    const apiFn = this.action;
    this.cachedDispatch(COURSES.GET_COURSE, { param: apiFn });
    this.cachedDispatch(STATES.GET_STATES, { param: apiFn });
  },
  mounted() {
    this.getJobList();
    this.getFeaturedPosts();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.scrollToHashSectionWhenReady();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    scrollToHashSectionWhenReady() {
      if (!isUpskillingCoursesHash(this.$route)) return;
      if (this.jobsLoading || this.postsLoading) return;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          scrollToSection('upskilling-courses', { behavior: 'auto', settle: true });
        });
      });
    },
    onScroll() {
      const isMobile = window.matchMedia('(max-width: 991px)').matches;
      if (isMobile) {
        this.showStickySearch = false;
        return;
      }
      this.showStickySearch = window.scrollY > 420;
    },
    getJobList() {
      this.action.getJobList(this.filter).then((res) => {
        this.jobsLoading = false;
        this.jobs = res.jobs.slice(0, 8).map((job) =>
          attachJobParam(job, this.validate, { truncateParam: true })
        );
      });
    },
    getFeaturedPosts() {
      this.postsLoading = true;
      this.action.getPosts(5).then((res) => {
        const posts = (res && (res.posts || res.data || res.result))
          ? (res.posts || res.data || res.result)
          : [];
        this.featuredPosts = Array.isArray(posts) ? posts.slice(0, 3) : [];
      }).catch(() => {
        this.featuredPosts = [];
      }).finally(() => {
        this.postsLoading = false;
      });
    },
    getJobs() {
      const searchQuery = {};
      if (this.filter.search) {
        searchQuery.search = this.filter.search;
      }
      if (this.filter.course_id) {
        searchQuery.course_id = this.filter.course_id;
      }
      if (this.filter.city_id) {
        searchQuery.city_id = this.filter.city_id;
      }
      this.$router.push({ path: '/jobsearch', query: searchQuery });
    },
  },
};
</script>
